import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiGrid, FiTrendingUp, FiZap } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const InfoModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const infoItems = [
    {
      icon: FiZap,
      title: "Sparks",
      description: "Each conversation you complete earns you a Spark. These represent meaningful moments and interactions you've shared.",
      color: "text-amber-500",
      bg: "bg-amber-50"
    },
    {
      icon: FiGrid,
      title: "Grid",
      description: "Complete 4 challenges in a row (horizontal, vertical, or diagonal) to create a Grid connection and unlock bonus celebrations!",
      color: "text-indigo-600",
      bg: "bg-indigo-50"
    },
    {
      icon: FiTrendingUp,
      title: "Progress Bar",
      description: "Track your journey through all 16 challenges in this session. Aim for 100% to master the art of connection!",
      color: "text-slate-900",
      bg: "bg-slate-50"
    }
  ];

  return (
    <AnimatePresence>
      <div 
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md"
        onClick={onClose}
      >
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-[2rem] shadow-2xl w-full max-w-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-6 bg-gradient-to-r from-indigo-500 to-cyan-500 text-white relative">
            <button 
              onClick={onClose}
              className="absolute top-6 right-6 p-2 text-white/70 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiX} className="w-6 h-6" />
            </button>
            <h2 className="text-2xl font-black">How ConnectGo Works</h2>
            <p className="text-sm text-white/80 mt-1 font-medium">Understanding your journey</p>
          </div>

          <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {infoItems.map((item, index) => (
              <div 
                key={index}
                className={`${item.bg} p-5 rounded-2xl border border-slate-100`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    <SafeIcon icon={item.icon} className={`w-5 h-5 ${item.color}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-black text-slate-900 mb-1">{item.title}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed font-medium">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            <div className="bg-gradient-to-br from-indigo-50 to-cyan-50 p-5 rounded-2xl border border-indigo-100 mt-6">
              <h3 className="text-sm font-black text-slate-900 mb-3 flex items-center gap-2">
                <span className="text-lg">🏆</span> Milestone Titles
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-700">0-9 Sparks</span>
                  <span className="text-slate-500 font-medium">New Explorer</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-700">10-24 Sparks</span>
                  <span className="text-slate-500 font-medium">Conversation Starter</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-700">25-49 Sparks</span>
                  <span className="text-slate-500 font-medium">Connection Builder</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-700">50-99 Sparks</span>
                  <span className="text-slate-500 font-medium">Community Weaver</span>
                </div>
                <div className="flex justify-between items-center text-xs">
                  <span className="font-bold text-slate-700">100+ Sparks</span>
                  <span className="text-indigo-600 font-black">Connection Champion</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6 border-t border-slate-100">
            <button 
              onClick={onClose}
              className="w-full py-3 bg-slate-900 text-white rounded-xl font-black text-sm hover:bg-slate-800 transition-colors active:scale-95"
            >
              Got it!
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default InfoModal;